import { Component, HostListener, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { DevolutionProjectionDetailTableModel } from 'src/app/models/tables/preliminaries/devolution-projection-detail-table.model';
import { DevolutionProjectionTableModel } from 'src/app/models/tables/preliminaries/devolution-projection-table.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { DevolutionProjectionDetailService } from 'src/app/services/preliminaries/devolution-projection-detail.service';
import { DevolutionProjectionService } from 'src/app/services/preliminaries/devolution-projection.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-devolution-projection',
  templateUrl: './devolution-projection.component.html',
  styleUrls: ['./devolution-projection.component.scss'],
  standalone: false
})
export class DevolutionProjectionComponent implements OnInit {

  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly devolutionProjectionService = inject(DevolutionProjectionService);
  private readonly devolutionProjectionDetailService = inject(DevolutionProjectionDetailService);
  private readonly router = inject(Router);
  private readonly headerService = inject(HeaderService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<DevolutionProjectionDetailTableModel> =
    new PagedResponseModel<DevolutionProjectionDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = []
  generalData: PagedResponseModel<DevolutionProjectionTableModel> =
    new PagedResponseModel<DevolutionProjectionTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined
  limit: number = 10;
  nameWindow: string = 'Proyección Devolución';
  page: number = 1;
  previousSort: { field: string; isAscsendant: number };
  projectName = ProjectName.projectName;
  permissionUpdatable: boolean = false;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];
  url: string = 'devolution-projection';

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(`${this.url}`);
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getPermission(),
      this.getColumnsForGeneralConsultation(),
      this.getAllDevolutionProjectionsWithPagination(),
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(this.devolutionProjectionDetailService.exportDetailConsultation(
        this.filters, this.columnSort, this.ascendant
      ));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Detalle Proyecciones de Devolución.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los detalles de las proyecciones de devolución.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(this.devolutionProjectionService.exportGeneralConsultation(
        this.filters, this.columnSort, this.ascendant
      ));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Proyecciones Devolución.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las proyecciones de devolución.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        const response = await firstValueFrom(this.devolutionProjectionDetailService.getColumnsForDetailConsultation());
        this.detailHeader = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta de detalles.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        const response = await firstValueFrom(this.devolutionProjectionService.getColumnsForGeneralConsultation());
        this.generalHeader = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta general.', error);
    }
  }

  async getAllDevolutionProjectionsWithPagination() {
    try {
      const response = await firstValueFrom(this.devolutionProjectionService.getAllDevolutionProjectionsWithPagination(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters
      ));
      this.generalData = response;
    } catch (error) {
      console.error('Error al obtener las proyecciones de devolución.', error);
    }
  }

  async getAllDevolutionProjectionDetailsWithPagination() {
    try {
      const response = await firstValueFrom(this.devolutionProjectionDetailService.getAllDevolutionProjectionDetailsWithPagination(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters
      ));
      this.detailData = response;
    } catch (error) {
      console.error('Error al obtener los detalles de las proyecciones de devolución.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await this.getPermissionProfileByUrl();
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;
    if (this.activeTabIndex === tabIndex) return;
    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;
    switch (this.activeTabIndex) {
      case 0: {
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getAllDevolutionProjectionsWithPagination()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      }
      case 1: {
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getAllDevolutionProjectionDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      }
    }
  }

  createNewRegister() {
    this.router.navigate([`/${this.url}/register`]);
  }

  onAction(event: { action: string, item?: DevolutionProjectionTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/${this.url}/register/${event.item.id}`]);
    };
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0: {
        await this.exportGeneralConsultation();
        break;
      }
      case 1: {
        await this.exportDetailConsultation();
        break;
      }
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0: {
        await this.getAllDevolutionProjectionsWithPagination()
        break;
      }
      case 1: {
        await this.getAllDevolutionProjectionDetailsWithPagination()
        break;
      }
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
